import type {
  AllComponents,
  WebpageComponentRelation,
} from "../../../../components/_default/interfaces/base";
import type { StructureMap } from "./structureReducer";

export type ComponentMap = Record<string, AllComponents>;
const EDITOR_SENTINEL_KEY = "-11";

/** Builder-only placeholder; must not appear in API payloads as a linked child. */
export function isBuilderEditorSentinelComponent(
  component: Pick<AllComponents, "id" | "type"> | null | undefined
): boolean {
  if (!component || typeof component !== "object") return false;
  return component.id === -11 || component.type === "editor";
}
type RelationLike = {
  id?: number | null;
  parent_id?: number | null;
  child_id?: number | null;
  parent_uuid?: string | null;
  child_uuid?: string | null;
  sort?: number | null;
  created_at?: Date | null;
  updated_at?: Date | null;
};

type ApiChildComponentLike = {
  childUuid?: string | null;
  sortOrder?: number | null;
};

type ApiComponentLike = AllComponents & {
  parentUuid?: string | null;
  sortOrder?: number | null;
  childComponents?: ApiChildComponentLike[] | null;
  languageCode?: string | null;
};

export function getCanonicalComponentKey(
  component: Pick<AllComponents, "id" | "uuid"> | null | undefined,
  fallbackKey?: string
): string | null {
  if (!component) return fallbackKey ?? null;
  if (component.id === -11) return EDITOR_SENTINEL_KEY;
  if (fallbackKey === EDITOR_SENTINEL_KEY) return EDITOR_SENTINEL_KEY;
  const uuid = component.uuid?.trim();
  if (uuid) return uuid;
  if (component.id != null) return String(component.id);
  return fallbackKey ?? null;
}

export function resolveComponentMapKey(
  components: ComponentMap | undefined,
  ref:
    | string
    | number
    | null
    | undefined
    | Pick<AllComponents, "id" | "uuid">
): string | null {
  if (!components || ref == null) return null;
  if (typeof ref === "object") {
    const direct = getCanonicalComponentKey(ref);
    if (direct && components[direct]) return direct;
    if (ref.id != null) {
      const idKey = String(ref.id);
      if (components[idKey]) return idKey;
    }
  } else {
    const k = String(ref).trim();
    if (!k) return null;
    if (components[k]) return k;
  }

  const wanted = typeof ref === "object" ? undefined : String(ref);
  for (const [key, value] of Object.entries(components)) {
    if (!value) continue;
    if (typeof ref === "object") {
      if (ref.uuid?.trim() && value.uuid?.trim() === ref.uuid.trim()) return key;
      if (ref.id != null && value.id === ref.id) return key;
      continue;
    }
    if (value.uuid?.trim() === wanted) return key;
    if (String(value.id) === wanted) return key;
  }
  return null;
}

export function getComponentByRef(
  components: ComponentMap | undefined,
  ref:
    | string
    | number
    | null
    | undefined
    | Pick<AllComponents, "id" | "uuid">
): AllComponents | undefined {
  const key = resolveComponentMapKey(components, ref);
  if (!components || !key) return undefined;
  return components[key];
}

export function getRelationChildKey(
  components: ComponentMap | undefined,
  relation: RelationLike | null | undefined
): string | null {
  if (!relation) return null;
  const relationChildUuid =
    typeof relation.child_uuid === "string" ? relation.child_uuid.trim() : "";
  if (relationChildUuid) {
    return (
      resolveComponentMapKey(components, relationChildUuid) ?? relationChildUuid
    );
  }
  if (relation.child_id != null) {
    return resolveComponentMapKey(components, relation.child_id) ?? String(relation.child_id);
  }
  return null;
}

export function withComponentAliases(
  components: ComponentMap | undefined
): ComponentMap {
  const out: ComponentMap = {};
  for (const [existingKey, component] of Object.entries(components ?? {})) {
    if (!component) continue;
    const canonical = getCanonicalComponentKey(component, existingKey);
    if (!canonical) continue;
    const normalized = {
      ...component,
      uuid: component.uuid?.trim() ? component.uuid : canonical,
    };
    out[canonical] = normalized as AllComponents;
  }
  return out;
}

function normalizeRelations(
  parent: AllComponents,
  componentsByCanonical: ComponentMap
): WebpageComponentRelation[] {
  const relations = Array.isArray(parent.childWebpageComponentRelations)
    ? parent.childWebpageComponentRelations
    : [];
  const normalizedRelations = relations
    .filter((relation): relation is WebpageComponentRelation & RelationLike =>
      !!relation && typeof relation === "object"
    )
    .map((relation, index) => {
    const childKey = getRelationChildKey(componentsByCanonical, relation);
    const child = childKey ? componentsByCanonical[childKey] : undefined;
    const parentUuid = parent.uuid?.trim() || null;
    const childUuid =
      child?.uuid?.trim() ||
      (typeof (relation as RelationLike).child_uuid === "string"
        ? (relation as RelationLike).child_uuid
        : null);
    const next: WebpageComponentRelation = {
      ...relation,
      parent_id: parent.id,
      child_id: child?.id ?? relation.child_id,
      sort:
        typeof relation.sort === "number" && Number.isFinite(relation.sort)
          ? relation.sort
          : index + 1,
      id: typeof relation.id === "number" ? relation.id : 0,
      created_at: relation.created_at ?? null,
      updated_at: relation.updated_at ?? null,
    };
    (next as any).parent_uuid = parentUuid;
    (next as any).child_uuid = childUuid;
    return next;
  });
  return normalizedRelations;
}

export function uniqueByCanonicalKey(
  components: ComponentMap | undefined
): ComponentMap {
  const out: ComponentMap = {};
  const relationCount = (component: AllComponents): number =>
    Array.isArray(component.childWebpageComponentRelations)
      ? component.childWebpageComponentRelations.filter((x) => !!x).length
      : 0;
  const childElementsCount = (component: AllComponents): number =>
    Array.isArray(component.childElements) ? component.childElements.length : 0;
  const componentScore = (component: AllComponents): number => {
    const attrs = component.attributes && typeof component.attributes === "object"
      ? Object.keys(component.attributes).length
      : 0;
    return relationCount(component) * 100 + childElementsCount(component) * 10 + attrs;
  };

  for (const [existingKey, component] of Object.entries(components ?? {})) {
    if (!component) continue;
    const canonical = getCanonicalComponentKey(component, existingKey);
    if (!canonical) continue;
    const normalized = {
      ...component,
      uuid: component.uuid?.trim() ? component.uuid : canonical,
    } as AllComponents;
    const current = out[canonical];
    if (!current || componentScore(normalized) >= componentScore(current)) {
      out[canonical] = normalized;
    }
  }
  return out;
}

function mapApiChildComponentsToRelations(
  component: ApiComponentLike
): WebpageComponentRelation[] {
  const rawChildren = Array.isArray(component.childComponents)
    ? component.childComponents
    : [];
  const orderedChildren = rawChildren
    .map((child, index) => ({ child, index }))
    .filter(({ child }) => {
      const childUuid =
        typeof child?.childUuid === "string" ? child.childUuid.trim() : "";
      return !!childUuid;
    })
    .sort((left, right) => {
      const leftSort =
        typeof left.child.sortOrder === "number" && Number.isFinite(left.child.sortOrder)
          ? left.child.sortOrder
          : Number.MAX_SAFE_INTEGER;
      const rightSort =
        typeof right.child.sortOrder === "number" && Number.isFinite(right.child.sortOrder)
          ? right.child.sortOrder
          : Number.MAX_SAFE_INTEGER;
      if (leftSort === rightSort) {
        return left.index - right.index;
      }
      return leftSort - rightSort;
    });
  const parentId =
    typeof component.parent_id === "number" && Number.isFinite(component.parent_id)
      ? component.parent_id
      : 0;

  return orderedChildren.map(({ child }, index) => ({
    id: 0,
    parent_id: parentId,
    child_id: 0,
    parent_uuid:
      typeof component.uuid === "string" && component.uuid.trim()
        ? component.uuid.trim()
        : null,
    child_uuid:
      typeof child.childUuid === "string" && child.childUuid.trim()
        ? child.childUuid.trim()
        : null,
    sort: index + 1,
    created_at: null,
    updated_at: null,
  }));
}

export function mapWebpageComponentsFromApi(
  structure: StructureMap | null | undefined
): StructureMap {
  const sourceComponents =
    structure?.components && typeof structure.components === "object"
      ? structure.components
      : {};
  const mappedComponents: ComponentMap = {};

  for (const [key, value] of Object.entries(sourceComponents)) {
    if (!value || typeof value !== "object") {
      continue;
    }
    const component = value as ApiComponentLike;
    const existingRelations = Array.isArray(component.childWebpageComponentRelations)
      ? component.childWebpageComponentRelations
      : [];
    const mappedRelations =
      existingRelations.length > 0
        ? existingRelations
        : mapApiChildComponentsToRelations(component);
    const parentUuid =
      typeof component.parentUuid === "string" ? component.parentUuid.trim() : "";
    const incomingSortOrder =
      typeof component.sortOrder === "number" && Number.isFinite(component.sortOrder)
        ? component.sortOrder
        : null;

    const {
      childComponents: _ignoredChildComponents,
      parentUuid: _ignoredParentUuid,
      sortOrder: _ignoredSortOrder,
      ...rest
    } = component;

    mappedComponents[key] = {
      ...rest,
      parent_uuid:
        (component as { parent_uuid?: string | null }).parent_uuid ??
        (parentUuid || null),
      sort_order:
        typeof component.sort_order === "number" && Number.isFinite(component.sort_order)
          ? component.sort_order
          : incomingSortOrder ?? 0,
      childWebpageComponentRelations: mappedRelations,
    } as AllComponents;
  }

  return {
    webpageComponents: Array.isArray(structure?.webpageComponents)
      ? [...structure.webpageComponents]
      : [],
    components: mappedComponents,
  };
}

/** PUT `/webpages/{uuid}/components` body shape (matches GET /components). */
export type WebpageComponentsApiPutPayload = {
  webpageComponents: string[];
  components: Record<string, Record<string, unknown>>;
};

function cloneAttributesForApi(
  attributes: AllComponents["attributes"] | null | undefined
): Record<string, unknown> {
  if (!attributes || typeof attributes !== "object") {
    return {};
  }
  try {
    return JSON.parse(JSON.stringify(attributes)) as Record<string, unknown>;
  } catch {
    return { ...(attributes as Record<string, unknown>) };
  }
}

/**
 * Serialize client {@link StructureMap} to the API component shape (parentUuid, sortOrder,
 * childComponents with childUuid/sortOrder). Caller should pass already-sanitized components.
 */
export function mapStructureMapToApiPutPayload(
  structure: StructureMap | null | undefined
): WebpageComponentsApiPutPayload {
  const source = structure?.components && typeof structure.components === "object"
    ? structure.components
    : {};
  const canonical = uniqueByCanonicalKey(source);
  const out: Record<string, Record<string, unknown>> = {};

  for (const [mapKey, component] of Object.entries(canonical)) {
    if (!component || typeof component !== "object") {
      continue;
    }
    if (isBuilderEditorSentinelComponent(component)) {
      continue;
    }

    const relations = Array.isArray(component.childWebpageComponentRelations)
      ? [...component.childWebpageComponentRelations].filter(
          (r): r is WebpageComponentRelation & RelationLike =>
            !!r && typeof r === "object" && r.child_id !== -11
        )
      : [];
    relations.sort(
      (a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0)
    );

    const childComponents = relations
      .map((rel, index) => {
        const childKey = getRelationChildKey(source, rel);
        const relUuid =
          typeof rel.child_uuid === "string" ? rel.child_uuid.trim() : "";
        let child =
          childKey != null ? source[childKey] ?? canonical[childKey] : undefined;
        if (!child && relUuid) {
          child = source[relUuid] ?? canonical[relUuid];
        }
        if (isBuilderEditorSentinelComponent(child)) {
          return null;
        }
        const childUuid = relUuid || (child?.uuid?.trim() ?? "");
        if (!childUuid) {
          return null;
        }
        return {
          childUuid,
          sortOrder: index,
        };
      })
      .filter((x): x is { childUuid: string; sortOrder: number } => !!x);

    const uuid = component.uuid?.trim() || mapKey;
    const parentUuidRaw =
      typeof (component as { parent_uuid?: string | null }).parent_uuid ===
      "string"
        ? (component as { parent_uuid?: string | null }).parent_uuid!.trim()
        : "";
    const sortOrder =
      typeof component.sort_order === "number" &&
      Number.isFinite(component.sort_order)
        ? component.sort_order
        : 0;
    const languageCode =
      typeof (component as { languageCode?: string | null }).languageCode ===
      "string"
        ? (component as { languageCode?: string | null }).languageCode
        : null;

    const api: Record<string, unknown> = {
      uuid,
      type: component.type,
      isShared: !!(component as { isShared?: boolean }).isShared,
      attributes: cloneAttributesForApi(component.attributes),
      childComponents,
      languageCode,
      sortOrder,
    };
    if (parentUuidRaw) {
      api.parentUuid = parentUuidRaw;
    }

    const outKey = getCanonicalComponentKey(component, mapKey) ?? uuid;
    out[outKey] = api;
  }

  const rootsRaw = Array.isArray(structure?.webpageComponents)
    ? structure.webpageComponents
    : [];
  const webpageComponents = rootsRaw
    .map((root) => resolveComponentMapKey(canonical, root) ?? String(root))
    .filter((root, i, arr) => root && arr.indexOf(root) === i);

  return { webpageComponents, components: out };
}

export function normalizeStructureForClient(
  structure: StructureMap | null | undefined
): StructureMap {
  const canonical = uniqueByCanonicalKey(structure?.components ?? {});

  for (const [key, component] of Object.entries(canonical)) {
    canonical[key] = {
      ...component,
      uuid: component.uuid?.trim() || key,
    };
  }

  for (const [key, component] of Object.entries(canonical)) {
    const parentKey =
      resolveComponentMapKey(canonical, component.parent_id) ??
      resolveComponentMapKey(canonical, (component as any).parent_uuid ?? null);
    const parent = parentKey ? canonical[parentKey] : undefined;
    canonical[key] = {
      ...component,
      parent_id: parent?.id ?? component.parent_id ?? null,
      parent_uuid: parent?.uuid?.trim() || (component as any).parent_uuid || null,
      childWebpageComponentRelations: normalizeRelations(component, canonical),
    } as AllComponents;
  }

  const rootsRaw = Array.isArray(structure?.webpageComponents)
    ? structure?.webpageComponents
    : [];
  const roots = rootsRaw
    .map((root) => resolveComponentMapKey(canonical, root) ?? String(root))
    .filter((root, index, array) => root && array.indexOf(root) === index);

  return {
    components: withComponentAliases(canonical),
    webpageComponents: roots,
  };
}

export function createUuidV7(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  const timestamp = BigInt(Date.now());
  bytes[0] = Number((timestamp >> 40n) & 0xffn);
  bytes[1] = Number((timestamp >> 32n) & 0xffn);
  bytes[2] = Number((timestamp >> 24n) & 0xffn);
  bytes[3] = Number((timestamp >> 16n) & 0xffn);
  bytes[4] = Number((timestamp >> 8n) & 0xffn);
  bytes[5] = Number(timestamp & 0xffn);

  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
